/**
 * Shared helpers used by ProjectCard (tile) and ProjectTable (list) so
 * monogram / status-pill / alert-text logic stays in lockstep across the
 * two views.
 */

/**
 * 2-char project monogram. Prefers the alpha prefix of project_number
 * (e.g. "CHICO-D-02" → "CH"); falls back to initials of project_name.
 */
export function getInitials(project) {
  const codePrefix = (project.project_number || '').split('-')[0];
  if (codePrefix.length >= 2) {
    return codePrefix.slice(0, 2).toUpperCase();
  }
  const words = (project.project_name || '?')
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Maps legacy status strings → StatusPill's 3-state model + a display
 * label. Unknown statuses fall through to draft styling with the raw
 * label so we never silently lose information.
 *
 * Mapping rationale:
 *   Active   → active  (live, success-toned)
 *   Planning → draft   (pre-construction, neutral)
 *   On Hold  → risk    (warning amber — needs attention)
 *   Complete → active  (with custom label, still success-toned)
 *   Closed   → draft   (with custom label, neutral)
 */
export function statusToPill(status) {
  switch (status) {
    case 'Active':
      return { status: 'active', label: 'Active' };
    case 'Planning':
      return { status: 'draft', label: 'Planning' };
    case 'On Hold':
      return { status: 'risk', label: 'On hold' };
    case 'Complete':
      return { status: 'active', label: 'Complete' };
    case 'Closed':
      return { status: 'draft', label: 'Closed' };
    default:
      return { status: 'draft', label: status || 'Active' };
  }
}

/**
 * Compact alert-chip text generator. Used for both project alerts
 * (tasks behind schedule) and dependency alerts (scheduling issues).
 *
 *   alertText(2, 1, 'task', 'behind schedule')
 *     → "2 tasks behind schedule (1 critical)"
 *
 *   alertText(3, 0, 'scheduling issue', '')
 *     → "3 scheduling issues"
 */
export function alertText(count, critical, noun, suffix) {
  const base = `${count} ${noun}${count === 1 ? '' : 's'}`;
  const tail = suffix ? ` ${suffix}` : '';
  const crit = critical > 0 ? ` (${critical} critical)` : '';
  return base + tail + crit;
}

/**
 * Convenience: PM column label is "Managers" if comma-separated,
 * otherwise "PM". Used by the tile detail grid.
 */
export function managerLabel(value) {
  return value && value.includes(',') ? 'Managers' : 'PM';
}
